import type { FormInstance, TableColumnsType } from 'antd'
import type { Dayjs } from 'dayjs'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'

import {
  CaretDownOutlined,
  CaretRightOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  AutoComplete,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  message
} from 'antd'
import { AxiosError } from 'axios'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useMemo, useReducer } from 'react'

import $api from '../../API'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'UTC'
})

interface Location {
  id: string

  key: string
  name: string
  short_name: string
  location: string

  created_at: string
  updated_at: string
}

export interface Holiday {
  id: string

  date: string
  description: string | null

  overrides: HolidayOverride[]

  created_at: string
  updated_at: string
}

interface HolidayOverride {
  location_id: string
  holiday_id: string

  date: string

  created_at: string
  updated_at: string
}

interface FormValues {
  date: Dayjs
  description: string
  overrides: {
    locations: Record<string, Dayjs>
  }
}

type MutationPayload = Pick<Holiday, 'id' | 'date' | 'description'> & {
  overrides: Array<Pick<HolidayOverride, 'location_id' | 'date'>>
}

interface State {
  expandedRow: {
    id?: string
  }
}

type Action = { type: 'set-expanded-row-id'; payload: string } | { type: 'clear-expanded-row-id' }

const initialState: State = {
  expandedRow: {
    id: undefined
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'set-expanded-row-id': {
      return {
        ...state,
        expandedRow: {
          ...state.expandedRow,
          id: action.payload
        }
      }
    }

    case 'clear-expanded-row-id': {
      return {
        ...state,
        expandedRow: {
          ...state.expandedRow,
          id: undefined
        }
      }
    }

    default: {
      return state
    }
  }
}

export function HolidaysListPage(): JSX.Element {
  const [form] = Form.useForm<FormValues>()
  const [state, dispatch] = useReducer(reducer, initialState)
  const [messageApi, contextHolder] = message.useMessage()

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    async queryFn() {
      try {
        const response = await $api.get('/locations')
        return response.data
      } catch (e) {
        if (e instanceof AxiosError) {
          throw new TypeError(`Request failed with status ${e.status} ${e.message}`)
        }
      }
    }
  })

  const query = useQuery<unknown, unknown, Holiday[]>({
    queryKey: ['holidays'],

    async queryFn() {
      const { data } = await $api.get<Holiday[]>('/holidays')

      return data
    }
  })

  const updateMutation = useMutation<unknown, unknown, MutationPayload>({
    async mutationFn({ id, ...data }: MutationPayload) {
      const isNew = id === 'new'

      const url = isNew ? '/holidays' : `/holidays/${id}`

      try {
        await $api.request({
          url,
          method: isNew ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          data
        })

        dispatch({
          type: 'clear-expanded-row-id'
        })

        void query.refetch()
      } catch (e) {
        if (e instanceof AxiosError && e.status === 409) {
          void messageApi.error('Holiday already exists')
          return
        }

        throw new Error('Failed to update holiday')
      }
    }
  })

  const deleteMutation = useMutation<unknown, unknown, string>({
    async mutationFn(id: string) {
      try {
        await $api.delete(`/holidays/${id}`, {
          method: 'DELETE'
        })
        dispatch({
          type: 'clear-expanded-row-id'
        })

        void query.refetch()
      } catch {
        throw new Error('Failed to delete holiday')
      }
    }
  })

  const handleRowExpand = useCallback(
    (expanded: boolean, holiday: Holiday) => {
      form.resetFields()

      if (!expanded) {
        dispatch({
          type: 'clear-expanded-row-id'
        })

        return
      }

      form.setFieldsValue({
        date: dayjs(holiday.date),
        description: holiday.description ?? '',
        overrides: {
          locations: Object.fromEntries(
            holiday.overrides.map(override => [override.location_id, dayjs(override.date)])
          )
        }
      })

      dispatch({
        type: 'set-expanded-row-id',
        payload: holiday.id
      })
    },
    [form]
  )

  const handleCancel = useCallback(() => {
    form.resetFields()
    dispatch({
      type: 'clear-expanded-row-id'
    })
  }, [form])

  const handleSave = useCallback(() => {
    const id = state.expandedRow.id
    const values = form.getFieldsValue()

    if (id == null) {
      return
    }

    updateMutation.mutate({
      id,
      date: values.date.format('YYYY-MM-DD'),
      description: values.description.trim() || null,
      overrides: Object.entries(values.overrides.locations)
        .filter(([_, date]) => !!date)
        .map(([locationId, date]) => ({
          location_id: locationId,
          date: date.format('YYYY-MM-DD')
        }))
    })
  }, [form, state.expandedRow.id, updateMutation])

  const handleDelete = useCallback(() => {
    const id = state.expandedRow.id

    if (!id) {
      return
    }

    deleteMutation.mutate(id)
  }, [state.expandedRow.id, deleteMutation])

  const columns = useMemo<TableColumnsType<Holiday>>(
    () => [
      {
        title: 'Date',
        key: 'date',
        dataIndex: 'date',
        width: '15%',
        render(value: string) {
          return dateFormatter.format(new Date(value))
        }
      },
      {
        title: 'Holiday',
        key: 'description',
        dataIndex: 'description',
        width: '25%',
        render(value: string | null) {
          if (!value) {
            return <span className='text-gray-300'>—</span>
          }

          return value
        }
      },
      ...(locationsQuery.data ?? []).map(location => ({
        title: location.short_name,
        key: `overrides.locations.${location.id}`,
        width: `${(100 - 40 - 5) / (locationsQuery.data?.length ?? 1)}%`,
        render(_: string, record: Holiday) {
          const override = record.overrides.find(override => override.location_id === location.id)

          if (!override) {
            return <span className='text-gray-300'>—</span>
          }

          return dateFormatter.format(new Date(override.date))
        }
      }))
    ],
    [locationsQuery.data]
  )

  const descriptions = useMemo(() => {
    return (
      query.data
        ?.map(holiday => holiday.description?.trim())
        .filter((v): v is NonNullable<typeof v> => typeof v === 'string' && v.length > 0)
        .sort((a, b) => a.localeCompare(b))
        .filter((v, i, a) => a.indexOf(v) === i) ?? []
    )
  }, [query.data])

  return (
    <>
      {contextHolder}

      {state.expandedRow.id === 'new' && (
        <Card
          className='w-full rounded-xl mb-4'
          bodyStyle={{
            padding: '0'
          }}
        >
          <ExpandedRow
            form={form}
            descriptions={descriptions}
            locations={locationsQuery.data ?? []}
            isSubmitting={updateMutation.isLoading || deleteMutation.isLoading}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </Card>
      )}

      <Card
        className='w-full rounded-xl relative'
        bodyStyle={{
          padding: '0'
        }}
      >
        <Button
          htmlType='button'
          type='link'
          className='absolute right-1 top-0.5 z-10'
          icon={<PlusCircleOutlined />}
          onClick={() => {
            if (state.expandedRow.id === 'new') {
              return
            }

            form.resetFields()
            dispatch({
              type: 'set-expanded-row-id',
              payload: 'new'
            })
          }}
        />

        <Table
          size='small'
          rowKey='id'
          dataSource={query.data ?? []}
          loading={query.isLoading || locationsQuery.isLoading}
          columns={columns}
          expandable={{
            expandedRowKeys: typeof state.expandedRow.id === 'string' ? [state.expandedRow.id] : [],
            expandedRowRender() {
              return (
                <ExpandedRow
                  form={form}
                  descriptions={descriptions}
                  locations={locationsQuery.data ?? []}
                  isSubmitting={updateMutation.isLoading || deleteMutation.isLoading}
                  onSave={handleSave}
                  onDelete={handleDelete}
                  onCancel={handleCancel}
                />
              )
            },
            expandIcon: ExpandIcon,
            onExpand: handleRowExpand,
            columnWidth: '5%'
          }}
          pagination={false}
        />
      </Card>
    </>
  )
}

interface ExpandedRowProps {
  form: FormInstance<FormValues>
  descriptions: string[]
  locations: Location[]
  isSubmitting: boolean
  onSave: () => void
  onCancel: () => void
  onDelete?: () => void
}

function ExpandedRow({
  form,
  descriptions,
  locations,
  isSubmitting,
  onSave,
  onCancel,
  onDelete
}: ExpandedRowProps): JSX.Element {
  return (
    <div className='p-4'>
      <div className='text-gray-400'>
        <Form
          form={form}
          layout='horizontal'
          className='p-4'
          colon={false}
          labelCol={{
            className: 'font-semibold',
            span: 2
          }}
          wrapperCol={{
            span: 22
          }}
        >
          <Form.Item name='date' label='Date' required>
            <DatePicker
              size='large'
              className='w-full rounded-md'
              placeholder='Select Date'
              allowClear={false}
              format='MM/DD/YYYY'
            />
          </Form.Item>

          <Form.Item name='description' label='Description'>
            <AutoComplete
              size='large'
              className='w-full rounded-md'
              placeholder="E.g. Martin Luther King's Birthday"
              allowClear
              options={descriptions.map(value => ({ value }))}
              filterOption={(inputValue, option) => {
                return option?.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
              }}
            />
          </Form.Item>

          {locations.length > 0 && (
            <>
              <Row className='pb-6'>
                <Col offset={2} span={22}>
                  <span className='text-base font-medium'>
                    Run day override{' '}
                    <Tooltip title='You can override the run day for a holiday on a per-location basis. If you do not override the run day, the run day will be the same as the holiday date.'>
                      <QuestionCircleOutlined className='-translate-y-0.5 translate-x-0.5' />
                    </Tooltip>
                  </span>
                </Col>
              </Row>

              {locations.map(location => (
                <Form.Item
                  key={location.id}
                  name={['overrides', 'locations', location.id]}
                  label={location.short_name}
                  tooltip={`Override run day for ${location.name}. Leave blank to use the holiday date.`}
                >
                  <DatePicker
                    size='large'
                    className='w-full rounded-md'
                    placeholder='Select Date'
                    allowClear
                    format='MM/DD/YYYY'
                  />
                </Form.Item>
              ))}
            </>
          )}

          <Row>
            <Col span={24} className='flex justify-end'>
              <Space>
                <Button
                  size='large'
                  type='link'
                  htmlType='reset'
                  className='rounded-md'
                  onClick={event => {
                    event.preventDefault()
                    onCancel()
                  }}
                >
                  Cancel
                </Button>

                {typeof onDelete === 'function' && (
                  <Popconfirm
                    title='Delete holiday'
                    description='You are about to delete this holiday. This action is irreversible. Do you wish to proceed?'
                    onConfirm={event => {
                      event?.preventDefault()
                      onDelete()
                    }}
                  >
                    <Button
                      danger
                      size='large'
                      type='default'
                      htmlType='button'
                      disabled={isSubmitting}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                )}

                <Button
                  size='large'
                  type='primary'
                  htmlType='submit'
                  disabled={isSubmitting}
                  onClick={event => {
                    event.preventDefault()
                    onSave()
                  }}
                >
                  Save
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  )
}

function ExpandIcon(props: RenderExpandIconProps<Holiday>): JSX.Element | null {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-50 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}
